import type { DocumentItem, DocumentType } from './model.js';
import { getValueAtPath } from './path.js';

/**
 * A field can be described three ways: a key of the source object, a dot path
 * string, or a function. The function form is what lets this library consume
 * essentially any backend shape without the consumer transforming their data.
 *
 * The function may return `undefined` for any field: real rows have missing
 * values, and `resolveMapper` already copes with every one of them (a missing
 * `id` falls back to a generated one, a missing `name` to `''`, and every
 * optional field is simply left off the item). Requiring `(item) => V` here
 * would force consumers to cast or invent a value — exactly the data-shaping
 * work this mapper exists to avoid.
 */
export type FieldMap<T, V> = keyof T | (string & {}) | ((item: T) => V | undefined);

export interface DocumentMapper<T = unknown> {
  id?: FieldMap<T, string | number>;
  name?: FieldMap<T, string>;
  type?: FieldMap<T, DocumentType | string>;
  parentId?: FieldMap<T, string | number | null>;
  /** Escape hatch for nested input; `normalize()` flattens it. */
  children?: FieldMap<T, T[]>;

  mimeType?: FieldMap<T, string>;
  extension?: FieldMap<T, string>;
  size?: FieldMap<T, number>;
  createdAt?: FieldMap<T, string | number | Date>;
  updatedAt?: FieldMap<T, string | number | Date>;
  viewUrl?: FieldMap<T, string>;
  downloadUrl?: FieldMap<T, string>;

  metadata?: FieldMap<T, Record<string, unknown>>;
}

/** Resolve one field descriptor against a source object. */
export function readField<T, V>(item: T, field: FieldMap<T, V> | undefined): unknown {
  if (field === undefined) return undefined;
  if (typeof field === 'function') return (field as (i: T) => V)(item);
  return getValueAtPath(item, field as string);
}

function toStringOrUndefined(v: unknown): string | undefined {
  if (v === undefined || v === null) return undefined;
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return undefined;
}

function toNumberOrUndefined(v: unknown): number | undefined {
  if (v === undefined || v === null || v === '') return undefined;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Coerce whatever the consumer calls a "type" into our two-value union.
 * Accepts `folder`/`FOLDER`/`directory`/`dir`, and booleans like `isFolder`.
 */
export function coerceType(v: unknown): DocumentType {
  if (v === true) return 'folder';
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (s === 'folder' || s === 'directory' || s === 'dir' || s === 'group') return 'folder';
  }
  return 'file';
}

/** Derive `.pdf` -> `pdf` from a filename when the source has no extension field. */
export function extensionFromName(name: string): string | undefined {
  const dot = name.lastIndexOf('.');
  if (dot <= 0 || dot === name.length - 1) return undefined;
  return name.slice(dot + 1).toLowerCase();
}

/**
 * Build a `(raw) => DocumentItem` function from a mapper.
 *
 * With no mapper, identity mapping applies, so data already in canonical shape
 * works with zero configuration.
 */
export function resolveMapper<T>(mapper?: DocumentMapper<T>): (raw: T, index: number) => DocumentItem {
  const m = mapper ?? ({} as DocumentMapper<T>);

  return (raw: T, index: number): DocumentItem => {
    const source = raw as unknown as Record<string, unknown>;

    const rawId = m.id !== undefined ? readField(raw, m.id) : source?.['id'];
    // A stable fallback id keeps one row without an id from breaking the tree.
    const id = toStringOrUndefined(rawId) ?? `__de_missing_${index}`;

    const name = toStringOrUndefined(m.name !== undefined ? readField(raw, m.name) : source?.['name']) ?? '';

    const typeValue = m.type !== undefined ? readField(raw, m.type) : source?.['type'];
    const type = coerceType(typeValue);

    const parentRaw = m.parentId !== undefined ? readField(raw, m.parentId) : source?.['parentId'];
    const parentId = parentRaw === undefined || parentRaw === null ? null : toStringOrUndefined(parentRaw) ?? null;

    const item: DocumentItem = { id, name, type, parentId, raw };

    const mimeType = toStringOrUndefined(m.mimeType !== undefined ? readField(raw, m.mimeType) : source?.['mimeType']);
    if (mimeType !== undefined) item.mimeType = mimeType;

    const extension =
      toStringOrUndefined(m.extension !== undefined ? readField(raw, m.extension) : source?.['extension']) ??
      (type === 'file' ? extensionFromName(name) : undefined);
    if (extension !== undefined) item.extension = extension.replace(/^\./, '').toLowerCase();

    const size = toNumberOrUndefined(m.size !== undefined ? readField(raw, m.size) : source?.['size']);
    if (size !== undefined) item.size = size;

    const createdAt = m.createdAt !== undefined ? readField(raw, m.createdAt) : source?.['createdAt'];
    if (createdAt !== undefined && createdAt !== null) item.createdAt = createdAt as DocumentItem['createdAt'];

    const updatedAt = m.updatedAt !== undefined ? readField(raw, m.updatedAt) : source?.['updatedAt'];
    if (updatedAt !== undefined && updatedAt !== null) item.updatedAt = updatedAt as DocumentItem['updatedAt'];

    const viewUrl = toStringOrUndefined(m.viewUrl !== undefined ? readField(raw, m.viewUrl) : source?.['viewUrl']);
    if (viewUrl !== undefined) item.viewUrl = viewUrl;

    const downloadUrl = toStringOrUndefined(
      m.downloadUrl !== undefined ? readField(raw, m.downloadUrl) : source?.['downloadUrl'],
    );
    if (downloadUrl !== undefined) item.downloadUrl = downloadUrl;

    const metadata = m.metadata !== undefined ? readField(raw, m.metadata) : source?.['metadata'];
    if (metadata && typeof metadata === 'object') item.metadata = metadata as Record<string, unknown>;

    return item;
  };
}
